'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { FC, useRef } from 'react';
import LogosMarquee from '@/components/Partnership/LogosMarquee';
import { RecommendSectionDto } from '@/components/Partnership/Recommend/types';
import AnimatedButton from '@/components/shared/AnimatedButton';
import { ContactUsDto } from '@/components/shared/Header/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useModal } from '@/hooks/useModal';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
import { ReferralDto } from './types';
interface Props {
  data: ReferralDto;
  dataRecommend?: RecommendSectionDto;
  contactUsData?: ContactUsDto;
}
const ReferralSection: FC<Props> = t0 => {
  const $ = _c(27);
  const {
    data,
    dataRecommend,
    contactUsData
  } = t0;
  const {
    title,
    text,
    logos,
    cta,
    isIndustry
  } = data;
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const titleRef = useRef(null);
  const topJawRef = useRef(null);
  const bottomJawRef = useRef(null);
  const contentRef = useRef(null);
  const {
    openModal
  } = useModal();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const section = sectionRef.current;
      const pinWrap = pinRef.current;
      const topJaw = topJawRef.current;
      const bottomJaw = bottomJawRef.current;
      const content = contentRef.current;
      if (!section || !topJaw || !bottomJaw || !content || !pinWrap) {
        return;
      }
      const mm = gsap.matchMedia();
      mm.add({
        isMobile: "(max-width: 990px)",
        isTablet: "(min-width: 991px) and (max-width: 1199px)",
        isDesktop: "(min-width: 1200px)"
      }, context => {
        const {
          isMobile,
          isTablet
        } = context.conditions as {
          isMobile: boolean;
          isTablet: boolean;
          isDesktop: boolean;
        };
        const jawOffset = isMobile ? 42 : isTablet ? 36 : 40;
        const jawRotate = isMobile ? 3 : 4;
        gsap.set(topJaw, {
          yPercent: 0,
          rotate: 0,
          transformOrigin: "center center"
        });
        gsap.set(bottomJaw, {
          yPercent: 0,
          rotate: 0,
          transformOrigin: "center center"
        });
        gsap.set(content, {
          opacity: 0.8,
          y: 40,
          scale: 0.94,
          filter: "blur(4px)"
        });
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: pinWrap,
            start: "top top",
            end: "+=600",
            scrub: true,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true
          }
        });
        tl.to(topJaw, {
          yPercent: -jawOffset,
          rotate: -jawRotate,
          ease: "none"
        }, 0).to(bottomJaw, {
          yPercent: jawOffset,
          rotate: jawRotate,
          ease: "none"
        }, 0).to(content, {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: "blur(0px)",
          ease: "none"
        }, 0.1);
      });
      return () => mm.revert();
    };
    t2 = {
      scope: sectionRef
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useGSAP(t1, t2);
  let t3;
  if ($[2] !== contactUsData || $[3] !== dataRecommend || $[4] !== openModal) {
    t3 = () => {
      if (dataRecommend) {
        openModal("RECOMMEND_US", {
          data: dataRecommend
        });
      } else {
        openModal("CONTACT_US", {
          data: contactUsData
        });
      }
      return null;
    };
    $[2] = contactUsData;
    $[3] = dataRecommend;
    $[4] = openModal;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const ctaHandler = t3;
  let t4;
  if ($[6] !== logos) {
    t4 = <div className={css.logosWrapper}><LogosMarquee data={logos} /></div>;
    $[6] = logos;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <img ref={topJawRef} src="/assets/images/partnership/referral-jaw/jaw-top.webp" alt="Decoration image" className={`${css.jaw} ${css.topJaw}`} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isIndustry || $[10] !== title) {
    t6 = title ? <Typography ref={titleRef} uppercase={true} className={css.title} variant={isIndustry ? "h2-industry" : "h2"} tag="h2"><StrapiRichContent content={title} /></Typography> : null;
    $[9] = isIndustry;
    $[10] = title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== text) {
    t7 = text ? <Typography className={css.text} variant="body-l" tag="div"><StrapiRichContent content={text} /></Typography> : null;
    $[12] = text;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = cta?.text || "recommend us";
  let t9;
  if ($[14] !== ctaHandler || $[15] !== t8) {
    t9 = <AnimatedButton className={css.contactButton} classNameText={css.contactButtonText} onClick={ctaHandler}>{t8}</AnimatedButton>;
    $[14] = ctaHandler;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t9) {
    t10 = <div ref={contentRef} className={css.content}>{t6}{t7}{t9}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <img ref={bottomJawRef} src="/assets/images/partnership/referral-jaw/jaw-bottom.webp" alt="Decoration image" className={`${css.jaw} ${css.bottomJaw}`} />;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10) {
    t12 = <div ref={pinRef} className={css.pinScene}><div className={css.inner}><div className={css.wrapperJaws}>{t5}{t10}{t11}</div></div></div>;
    $[22] = t10;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t4) {
    t13 = <section ref={sectionRef} className={css.section}>{t4}{t12}</section>;
    $[24] = t12;
    $[25] = t4;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
};
export default ReferralSection;